import * as Phaser from 'phaser';
import { SCENES, type GameOverPayload } from '@/types';
import { GAME_HEIGHT, GAME_WIDTH } from '@/config/gameConfig';
import { getMockRank, saveGameResult } from '@/utils/storage';

const COLORS = {
  primary: 0x3b9f4a,
  primaryHover: 0x2f8a3d,
  share: 0xffa726,
  text: '#ffffff',
  bg: '#1a1a2e',
} as const;

const HEART_COUNT = 5;
const HEART_R = 8;
const HEART_GAP = 22;

interface Summary {
  score: number;
  bestScore: number;
  isNewBest: boolean;
  maxStage: number;
  rank: number;
  reason: GameOverPayload['reason'];
}

export class GameOverScene extends Phaser.Scene {
  private payload?: GameOverPayload;

  constructor() {
    super(SCENES.GameOver);
  }

  init(data: GameOverPayload): void {
    this.payload = data;
  }

  create(): void {
    if (!this.payload) {
      this.scene.start(SCENES.Intro);
      return;
    }
    this.cameras.main.setBackgroundColor(COLORS.bg);

    const persisted = saveGameResult({
      score: this.payload.score,
      maxStage: this.payload.maxStage,
    });
    const summary: Summary = {
      score: this.payload.score,
      bestScore: persisted.bestScore,
      isNewBest: persisted.isNewBest,
      maxStage: this.payload.maxStage,
      rank: getMockRank(this.payload.score),
      reason: this.payload.reason,
    };

    this.drawHeader(summary);
    this.drawScore(summary);
    this.drawStats(summary);
    this.drawButtons();
    this.fadeInAll();
  }

  private drawHeader(summary: Summary): void {
    const titleText = summary.reason === 'tiny-hit' ? 'Thua rồi!' : 'Hết giờ!';
    this.add
      .text(GAME_WIDTH / 2, 70, titleText, {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '32px',
        fontStyle: 'bold',
        color: COLORS.text,
      })
      .setOrigin(0.5);

    if (summary.isNewBest) {
      const badge = this.add
        .text(GAME_WIDTH / 2, 105, '⭐ Kỷ lục mới!', {
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: '14px',
          fontStyle: 'bold',
          color: '#ffd84a',
        })
        .setOrigin(0.5);
      this.tweens.add({
        targets: badge,
        scale: { from: 1, to: 1.08 },
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private drawScore(summary: Summary): void {
    const scoreObj = { value: 0 };
    const scoreText = this.add
      .text(GAME_WIDTH / 2, 170, '0', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '72px',
        fontStyle: 'bold',
        color: COLORS.text,
      })
      .setOrigin(0.5)
      .setShadow(0, 4, '#0008', 6, true, true);

    this.add
      .text(GAME_WIDTH / 2, 215, 'điểm', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '13px',
        color: '#cccccc',
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: scoreObj,
      value: summary.score,
      duration: 900,
      ease: 'Cubic.easeOut',
      onUpdate: () => scoreText.setText(String(Math.round(scoreObj.value))),
      onComplete: () => scoreText.setText(String(summary.score)),
    });
  }

  private drawStats(summary: Summary): void {
    this.add
      .text(GAME_WIDTH / 2, 260, `Kỷ lục: ${summary.bestScore}`, {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '15px',
        color: '#fff8e1',
      })
      .setOrigin(0.5);

    this.add
      .text(GAME_WIDTH / 2, 285, 'Cấp cao nhất', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '12px',
        color: '#cccccc',
      })
      .setOrigin(0.5);

    const totalWidth = (HEART_COUNT - 1) * HEART_GAP;
    const startX = GAME_WIDTH / 2 - totalWidth / 2;
    for (let i = 0; i < HEART_COUNT; i++) {
      const x = startX + i * HEART_GAP;
      const filled = i <= summary.maxStage;
      this.add
        .arc(x, 312, HEART_R, 0, 360, false, filled ? 0xffd84a : 0xffffff, filled ? 1 : 0.18)
        .setStrokeStyle(1.5, 0xffffff, 0.85);
    }

    this.add
      .text(GAME_WIDTH / 2, 350, `Bạn xếp #${summary.rank} tuần này`, {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '14px',
        color: '#ffa726',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private drawButtons(): void {
    this.makeButton(440, 'CHƠI LẠI', COLORS.primary, COLORS.primaryHover, () =>
      this.scene.start(SCENES.Game),
    );
    this.makeButton(520, 'CHIA SẺ', COLORS.share, 0xe89020, () => {
      window.alert('Tính năng chia sẻ đang phát triển');
    });
    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 30, 'Quay về menu', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '12px',
        color: '#aaaaaa',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => this.scene.start(SCENES.Intro));
  }

  private makeButton(
    y: number,
    label: string,
    color: number,
    hoverColor: number,
    onClick: () => void,
  ): void {
    const x = GAME_WIDTH / 2;
    const w = 220;
    const h = 56;
    const draw = (c: number) => {
      btn.clear();
      btn.fillStyle(c, 1);
      btn.fillRoundedRect(x - w / 2, y - h / 2, w, h, 28);
      btn.lineStyle(3, 0xffffff, 1);
      btn.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 28);
    };
    const btn = this.add.graphics();
    draw(color);
    const text = this.add
      .text(x, y, label, {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    const hit = this.add
      .zone(x, y, w, h)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => draw(hoverColor));
    hit.on('pointerout', () => draw(color));
    hit.on('pointerdown', () => {
      this.tweens.add({ targets: [btn, text], scale: 0.95, duration: 80, yoyo: true });
    });
    hit.on('pointerup', onClick);
  }

  private fadeInAll(): void {
    this.cameras.main.setAlpha(0);
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 1,
      duration: 350,
      ease: 'Quad.easeOut',
    });
  }
}
